import { AutocompleteOptions as AutocompleteCoreOptions, BaseItem, GetSourcesParams } from '@algolia/autocomplete-core';
import { GetSources, OnStateChangeProps } from '@algolia/autocomplete-js';
import { createAlgoliaInsightsPlugin } from '@algolia/autocomplete-plugin-algolia-insights';
import { AutocompleteOptions as AutoCompleteSharedOptions } from '@algolia/autocomplete-shared';
import { MultipleQueriesQuery } from '@algolia/autocomplete-shared/dist/esm/preset-algolia/algoliasearch';
import { ErrorReporter } from '@mode/shared/contract-common';
import { SearchClient } from 'algoliasearch';
import insightsClient from 'search-insights';
import { AutocompleteSourceFactory } from './autocomplete-source.factory';
import { AutocompleteRender } from '@algolia/autocomplete-shared/dist/esm/js/AutocompleteRender';
import {
  AutocompleteFiltersWrapperElement,
  AutocompleteFiltersWrapperPreactComponent,
} from '../autocomplete-filters-wrapper/autocomplete-filters-wrapper.preact.component';
import { createRef, Fragment } from 'preact';
import { FacetTags, AutocompleteTagsService } from './autocomplete-tags.service';
import { AnalyticsEvent, AnalyticsService } from '@mode/shared/util-segment';
import { Facet, FacetFilter } from './autocomplete-helpers';
import { Observable } from 'rxjs';
import { GlobalSearchViewModel } from '../global-search.store';

export class AutocompleteOptionsFactory {
  filtersWrapperRef = createRef<AutocompleteFiltersWrapperElement>();
  vm!: GlobalSearchViewModel;

  constructor(
    private vm$: Observable<GlobalSearchViewModel>,
    private errorReporterService: ErrorReporter,
    private autocompleteTagsService: AutocompleteTagsService,
    private algoliaAppId: string,
    private algoliaApiKey: string,
    private analyticsService: AnalyticsService,
    private onRefUpdate: (elem: AutocompleteFiltersWrapperElement) => void,
    private readonly orgToken: string
  ) {
    vm$.subscribe((state) => (this.vm = state));
  }

  createOptions<TItem extends BaseItem>({
    container,
    searchClient,
    reportIndexName,
    collectionIndexName,
  }: {
    container: AutoCompleteSharedOptions<TItem>['container'];
    searchClient: SearchClient;
    reportIndexName: string;
    collectionIndexName: string;
  }): AutocompleteCoreOptions<TItem> & AutoCompleteSharedOptions<TItem> {
    return {
      container,
      detachedMediaQuery: '',
      insights: true,
      openOnFocus: true,
      placeholder: 'Search...',
      plugins: [createAlgoliaInsightsPlugin({ insightsClient }), this.autocompleteTagsService.algoliaTagsPlugin],
      getSources: (params) => {
        const tags = this.autocompleteTagsService.algoliaTagsPlugin.data?.tags;
        const queries = this.createQueries({
          query: params.query,
          reportIndexName,
          collectionIndexName,
          tags,
        });
        const hasNoAttrTags = this.autocompleteTagsService.getTagsByType(tags || [], 'attr').length === 0;
        // if no tags are selected don't show results
        const showNoResults = !!tags && hasNoAttrTags;

        return this.getSources<TItem>({
          ...params,
          searchClient,
          queries,
          showNoResults,
        });
      },
      onStateChange: (props: OnStateChangeProps<TItem>) => {
        // has the dialog just opened?
        if (!props.prevState.isOpen && props.state.isOpen) {
          this.onOpen<TItem>(props);
        }
        // has the dialog just closed?
        if (props.prevState.isOpen && !props.state.isOpen) {
          this.onClose();
        }
      },
      renderNoResults: (...params) => this.renderNoResult(...params),
      render: (...params) => this.render(...params),
    };
  }

  private onOpen<TItem extends BaseItem>(props: OnStateChangeProps<TItem>): void {
    // attempt to force Angular element to hard reload
    this.filtersWrapperRef.current?.reloadFilters();
    // clear previous search query whenever the dialog open.
    props.state.query = '';
    // track open
    this.analyticsService.track(AnalyticsEvent.Name.Search, {
      location: 'Spotlight Search',
      action: 'Open Search',
    });
  }

  private onClose(): void {
    // track closed
    this.analyticsService.track(AnalyticsEvent.Name.Search, {
      location: 'Spotlight Search',
      action: 'Closed Search',
    });
  }

  public createQueries({
    query,
    reportIndexName,
    collectionIndexName,
    tags,
  }: {
    query: string;
    reportIndexName: string;
    collectionIndexName: string;
    tags: FacetTags | undefined;
  }): MultipleQueriesQuery[] {
    const createReportQuery = this.createReportQueryFactory(reportIndexName);
    const createCollectionQuery = this.createCollectionQueryFactory(collectionIndexName);
    const reportQuery = createReportQuery(query, tags);
    const collectionQuery = createCollectionQuery(query, tags);

    return [reportQuery, ...(collectionQuery ? [collectionQuery] : [])];
  }

  public mapToAlgoliaNegativeFilters(tags: FacetTags, facetsToNegate: string[], operator = 'AND') {
    return tags
      .map(({ label, facet }) => facetsToNegate.includes(facet) && `NOT ${facet}:"${label}"`)
      .filter(Boolean)
      .join(` ${operator} `);
  }

  public createReportQueryFactory(
    indexName: string
  ): (query: string, tags: FacetTags | undefined) => MultipleQueriesQuery {
    return (query: string, tags: FacetTags | undefined) => {
      const attributeTags = this.autocompleteTagsService.getTagsByType(tags || [], 'attr');
      const filterTags = this.autocompleteTagsService.getTagsByType(tags || [], 'filter');
      const filters = this.createReportQueryFilter(this.orgToken, filterTags);
      const restrictSearchableAttributes = this.createQueryRestrictedSearchableAttrs(attributeTags, [
        'name',
        'description',
        'queryKeywords',
        'reportCreatorName',
      ]);

      return {
        indexName,
        query,
        params: {
          hitsPerPage: 5,
          restrictSearchableAttributes,
          getRankingInfo: true,
          attributesToRetrieve: [
            'collectionDefaultAccess',
            'collectionName',
            'description',
            'name',
            'queryKeywords',
            'reportType',
            'reportCreatorName',
            'token',
            'webPath',
            'archived',
          ],
        },
        filters,
      };
    };
  }

  public createCollectionQueryFilter(orgToken: string, tags: FacetTags): string {
    return FacetFilter.make([
      Facet.make({ name: `organization_${orgToken}` }),
      ...tags
        .filter((t) => t.facet === 'reportCreatorUsername')
        .map(({ facet: name, label }) => Facet.make({ name: 'creatorUsername', label })),
    ]);
  }

  public createReportQueryFilter(orgToken: string, tags: FacetTags): string {
    return FacetFilter.make([
      Facet.make({ name: `organization_${orgToken}` }),
      Facet.make({ name: 'archived', label: true, operator: 'NOT' }),
      ...tags.map(({ facet: name, label }) =>
        Facet.make({ name, label, operator: name === 'reportType' ? 'NOT' : null })
      ),
    ]);
  }

  public createQueryRestrictedSearchableAttrs(tags: FacetTags, allowed: string[] = []) {
    return tags
      .filter((t) => t.type === 'attr')
      .filter((t) => !allowed.length || allowed.includes(t.label))
      .map((t) => t.label);
  }

  public createCollectionQueryRestrictedSearchableAttrs(tags: FacetTags) {
    const collectionTags = tags.map((t) => {
      if (t.label === 'reportCreatorName') {
        return {
          ...t,
          label: 'createdBy',
        };
      }
      return t;
    });
    const allowedCollectionAttrs = ['name', 'createdBy'];

    return this.createQueryRestrictedSearchableAttrs(collectionTags, allowedCollectionAttrs);
  }

  public createCollectionQueryFactory(
    indexName: string
  ): (query: string, tags: FacetTags | undefined) => MultipleQueriesQuery | undefined {
    const hasCollectionTag = (tags: FacetTags | undefined) => tags?.some((tag) => tag.label === 'collections');
    return (query: string, tags: FacetTags | undefined) => {
      const attributeTags = this.autocompleteTagsService.getTagsByType(tags || [], 'attr');
      const filterTags = this.autocompleteTagsService.getTagsByType(tags || [], 'filter');
      const filters = this.createCollectionQueryFilter(this.orgToken, filterTags);
      const restrictSearchableAttributes = this.createCollectionQueryRestrictedSearchableAttrs(attributeTags);

      if (hasCollectionTag(tags)) {
        return;
      }

      return {
        indexName,
        query,
        params: {
          attributesToRetrieve: ['name', 'description', 'defaultAccess', 'spaceType', 'token', 'webPath', 'createdBy'],
          getRankingInfo: true,
          hitsPerPage: 5,
          restrictSearchableAttributes,
        },
        filters,
      };
    };
  }

  public getSources<TItem extends BaseItem>({
    searchClient,
    queries,
    showNoResults,
  }: Partial<GetSourcesParams<TItem>> & {
    searchClient: SearchClient;
    queries: MultipleQueriesQuery[];
    showNoResults: boolean;
  }): ReturnType<GetSources<TItem>> {
    const sourceFactory = new AutocompleteSourceFactory(
      this.errorReporterService,
      this.algoliaAppId,
      this.algoliaApiKey,
      this.analyticsService
    );
    const source = sourceFactory.createSource<TItem>({ searchClient, queries, showNoResults });

    return [source];
  }

  public render<TItem extends BaseItem>(
    ...[{ render, html, sections, state }, root]: Parameters<AutocompleteRender<TItem>>
  ) {
    const { contentTypeItems, matchOnItems, selectedContentTypeItems, selectedMatchOnItems } = this.vm;

    render(
      html`
        <${Fragment}>
          <!-- items -->
          <${AutocompleteFiltersWrapperPreactComponent}
            ref=${this.handleRefUpdate.bind(this)}
            contentTypeItems=${contentTypeItems}
            matchOnItems=${matchOnItems}
            selectedContentTypeItems=${selectedContentTypeItems}
            selectedMatchOnItems=${selectedMatchOnItems}
          />
          <div class="aa-PanelLayout aa-Panel--scrollable">
            ${sections}
          </div>
        </${Fragment}>
      ` as any,
      root
    );
  }

  public renderNoResult<TItem extends BaseItem>(
    ...[{ render, html, sections, state }, root]: Parameters<AutocompleteRender<TItem>>
  ) {
    const { contentTypeItems, matchOnItems, selectedContentTypeItems, selectedMatchOnItems } = this.vm;

    render(
      html`
        <${Fragment}>
          <!-- items -->
          <${AutocompleteFiltersWrapperPreactComponent}
            ref=${this.handleRefUpdate.bind(this)}
            contentTypeItems=${contentTypeItems}
            matchOnItems=${matchOnItems}
            selectedContentTypeItems=${selectedContentTypeItems}
            selectedMatchOnItems=${selectedMatchOnItems}
          />
          <div class="no-search-results">
            No results found
          </div>
          <div class="aa-SourceFooter">${AutocompleteSourceFactory.footer({ html })}</div>
        </${Fragment}>
      ` as any,
      root
    );
  }

  public handleRefUpdate(elem: AutocompleteFiltersWrapperElement | null) {
    if (elem) {
      // temporarily update local ref until event handling is migrated to global search component
      this.filtersWrapperRef.current = elem;
      // send updated {@link ComponentChildren} to parent
      this.onRefUpdate(elem);
    }
  }
}
